import { useRef, useState } from 'react'
import { supabase, readableError } from '../lib/supabase'
import { initials } from './ui'

const MAX_DIM = 512

/**
 * Downscale in the browser before uploading. A phone photo is 4–8 MB and 4000px
 * wide; nobody needs that for a 72px circle, and shrinking first means the
 * upload finishes instantly instead of stalling on mobile data.
 *
 * Also strips EXIF as a side effect of re-encoding through a canvas — a photo
 * straight off a phone can carry GPS coordinates, and a worship writer's home
 * location is not something to store by accident.
 */
async function downscale(file) {
  const bitmap = await createImageBitmap(file).catch(() => null)
  if (!bitmap) return file // exotic format (HEIC on some browsers) — send as is

  const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height))
  const w = Math.round(bitmap.width * scale)
  const h = Math.round(bitmap.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = w; canvas.height = h
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h)
  bitmap.close?.()

  const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.85))
  if (!blob || blob.size >= file.size) return file
  return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })
}

/**
 * PUT the file to R2 over XHR rather than fetch, for two reasons.
 *
 * One: fetch cannot report upload progress, and a phone on bad data spends
 * several silent seconds here — long enough that the only feedback ("…") reads
 * as a hang.
 *
 * Two, and the reason this function exists at all: fetch collapses every
 * network-layer refusal into one bare `TypeError: Failed to fetch`, with no way
 * to tell a CORS rejection from an offline phone. That is exactly the failure
 * we hit, and it is why the first report of it carried no useful detail. XHR
 * distinguishes them — a rejected preflight fires `onerror` with status 0,
 * where an HTTP error arrives with a real status — so the message below can
 * name the actual cause instead of shrugging.
 */
function putToR2(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url, true)
    xhr.setRequestHeader('Content-Type', contentType)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve()
      // R2 answers with an XML <Error><Message>, which is worth surfacing.
      const detail = /<Message>([^<]+)<\/Message>/.exec(xhr.responseText || '')?.[1]
      reject(new Error(
        `Storage refused the upload (${xhr.status})${detail ? ` — ${detail}` : ''}.`,
      ))
    }
    xhr.onerror = () => reject(new Error(
      "Couldn't reach the storage service. If you're online, this is the " +
      'bucket refusing the request from this site rather than anything you did.',
    ))
    xhr.ontimeout = () => reject(new Error('The upload timed out. Try again on a better connection.'))
    xhr.timeout = 60_000
    xhr.send(file)
  })
}

export default function AvatarUpload({ profile, url, onChange }) {
  const input = useRef(null)
  const [busy, setBusy] = useState(false)
  const [pct, setPct] = useState(null)
  const [err, setErr] = useState(null)

  const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ')

  const pick = async (chosen) => {
    if (!chosen) return
    setBusy(true); setErr(null); setPct(null)
    try {
      const file = await downscale(chosen)

      const { data, error } = await supabase.functions.invoke('r2-avatar-url', {
        body: { mode: 'upload', filename: file.name, content_type: file.type, size_bytes: file.size },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)

      setPct(0)
      await putToR2(data.upload_url, file, data.content_type, setPct)
      setPct(null)

      const { error: e2 } = await supabase.from('users')
        .update({ avatar_object_key: data.object_key }).eq('id', profile.id)
      if (e2) throw e2

      await onChange(data.object_key)
    } catch (e) {
      // These messages are already written for the person reading them;
      // readableError would strip them at the first colon.
      setErr(e?.name === 'Error' && e.message ? e.message : readableError(e))
    } finally {
      setBusy(false)
      setPct(null)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className="avatarpick">
      <button className="avatarbig" onClick={() => input.current?.click()} disabled={busy}
              aria-label="Change your picture">
        {url
          ? <img src={url} alt="" />
          : <span>{initials(name || profile?.email || '?')}</span>}
        <span className="avatarbig-edit">
          {pct !== null ? `${pct}%` : busy ? '…' : 'Edit'}
        </span>
      </button>
      <input ref={input} type="file" hidden accept="image/*"
             onChange={(e) => pick(e.target.files?.[0])} />
      {pct !== null && (
        <div className="uploadbar" role="progressbar" aria-valuenow={pct}
             aria-valuemin={0} aria-valuemax={100} aria-label="Upload progress">
          <span style={{ width: `${pct}%` }} />
        </div>
      )}
      {err && <p className="err">{err}</p>}
    </div>
  )
}
